/* eslint-disable no-param-reassign */
import Promise from 'bluebird';
import _ from 'underscore';
import { odata } from '@bingads-webui/http-util';
import * as url from '@bingads-webui/url-util';
import { get } from '@bingads-webui-universal/primitive-utilities';
import {
  DataRecord,
  odataQuery,
  odataUri,
  odataCast,
  QueryOptions,
  QueryResult,
} from '@bingads-webui/odata-converter';

import {
  AdapterBatchInvoker,
  AfterInject,
  IAdapterBatchRequest,
  IEdmEntitySet,
  IODataDefinition,
  IODataRequestParam,
  IODataOptions,
  ObjectDecorator,
} from './types';

interface ODataResponse {
  '@odata.count'?: number;
  value?: DataRecord[] | string[] | DataRecord;
  [property: string]: unknown;
}

interface FindOptions {
  data?: QueryResult;
  path?: string;
}

function sendBatchRequest(
  definition: IODataDefinition,
  params: IODataRequestParam[],
  options: IODataOptions
): Promise<unknown> {
  const reqs: IAdapterBatchRequest[] = _.map(params, (param: IODataRequestParam) => {
    const id = param.Id;
    const uri = odataUri.convert({ definition, options, id });

    return {
      type: options.action as string,
      url: uri,
      data: param,
    };
  });

  // The legacy HTTP shim requires config-supplied fields that this adapter intentionally omits.
  const batch = odata.batch as unknown as AdapterBatchInvoker;
  return batch({ reqs, path: options.path }).then((response: unknown) => {
    if (_.isFunction(options.processResponse)) {
      options.processResponse(response);
    }
    return response;
  });
}

function getCommonOptions({
  ajaxOptions = {},
  urlParameters,
  host,
  path,
  headers,
  successCallback,
  state,
  oDataToken,
}: IODataOptions = {}): IODataOptions {
  const options: IODataOptions = _.defaults({}, ajaxOptions);
  if (_.isObject(urlParameters)) {
    options.urlParameters = urlParameters;
  }
  if (host) {
    options.host = host;
  }
  if (path) {
    options.path = path;
  }
  if (oDataToken) {
    options.oDataToken = oDataToken;
  }
  if (headers) {
    options.headers = headers;
  }
  // <TODO> [lizho] Remove this after related OData APIs are refactored.
  // This callback is to get response header for product offer/product group related OData APIs.
  if (_.isFunction(successCallback)) {
    options.success = successCallback;
  }
  // <TODO>
  if (state) {
    options.params = _.clone(state.attributes);
  }
  return options;
}

export class ODataBaseAdapter {
  options: IODataOptions;
  // All of the methods shown here must return a promise

  // "definition" is a resource defintion that would
  // be returned by DS#defineResource

  // "options" would be the options argument that
  // was passed into the DS method that is calling
  // the adapter method
  constructor(options: IODataOptions = {}) {
    this.options = options;
  }

  convertQuery(queryParameters: QueryOptions): QueryResult {
    return odataQuery.convert(queryParameters);
  }

  create(definition: IODataDefinition, attrs: DataRecord, options: IODataOptions): Promise<unknown> {
    _.extendOwn(options, this.options);
    let data = odataCast.data({ data: attrs, definition, options });
    const { urlParameters: initialUrlParameters } = options;
    let urlParameters: QueryResult | IODataOptions['urlParameters'] = initialUrlParameters;

    // Below if block is for the calls being made without EDM models
    // This enables making "POST" calls with query parameters without edm.
    if (attrs.queryParams) {
      urlParameters = this.convertQuery(attrs.queryParams as QueryOptions);
      data = _.omit(data, 'queryParams');
    }

    // Must resolve the promise with the created item
    // NOTICE: added ajaxOptions here.
    return odata
      .post(odataUri.convert({ definition, options }), _.defaults({ data, urlParameters }, getCommonOptions(options)));
  }

  find(definition: IODataDefinition, id: string | number, options: IODataOptions): Promise<unknown> {
    _.extendOwn(options, this.options);
    let params: FindOptions = {};
    const { queryParameters } = options;

    if (_.isObject(queryParameters)) {
      params = { data: _.omit(this.convertQuery(queryParameters), '$top', '$count') as QueryResult };
    }

    params.path = options.path;

    // Must resolve the promise with the found item
    return odata.get(odataUri.convert({
      definition, options, id, isFinding: true,
    }), _.defaults(params, getCommonOptions(options)));
  }

  findAll(definition: IODataDefinition, params: QueryOptions, options: IODataOptions = {}): Promise<unknown> {
    // Must resolve the promise with the found items
    _.extendOwn(options, this.options);
    const { filterOptions } = options;

    // params.entitySet is flavored. options.entitySet is only surported for backward support.
    const queryEntitySet = get(params, 'query.entitySet');
    const entitySet = options.edmRootResourceIdentifier && queryEntitySet ?
      options.edmRootResourceIdentifier.identifyEntitySet(queryEntitySet) :
      get(options, 'entitySet');

    let request: Promise<unknown>;

    if (entitySet && options.edm && (get(entitySet, 'navigation.source.type') as object | undefined) instanceof options.edm.types.ActionType) {
      const actionEntitySet = entitySet as IEdmEntitySet;
      request = odata
        .post(
          url.queryify(
            odataUri.convert({
              definition, options, isFinding: true, entitySet: actionEntitySet,
            }),
            this.convertQuery(_.extend({ filterOptions }, params)),
            options.ajaxOptions!.customParameterizationFunc
          ),
          _.defaults({ data: actionEntitySet.navigation.parameters }, getCommonOptions(options))
        );
      options.cacheResponse = false;
    } else {
      request = odata.get(
        odataUri.convert({
          definition, options, isFinding: true, entitySet: entitySet as IEdmEntitySet | undefined,
        }),
        _.defaults({ data: this.convertQuery(_.extend({ filterOptions }, params)) }, getCommonOptions(options))
      );
    }

    return request.then((res: unknown) => {
      if (!res) {
        return null;
      }

      const response = res as ODataResponse;
      const addPropertyTotalCount = (obj: object) => Object.defineProperty(obj, 'totalCount', {
        value: response['@odata.count'],
        enumerable: false,
      });

      const addPropertyRaw = (obj: object) => Object.defineProperty(obj, 'raw', {
        value: res,
        enumerable: false,
      });

      const queryId = _.uniqueId('query-');
      // if OData results is just an array of string, make it an object then assign the object to value.
      if (_.isString(_.first(response.value as string[]))) {
        response.value = _.map(response.value as string[], (item: string) => ({
          id: _.uniqueId('item-'),
          value: item,
        }));
      }

      // JSData requires ID for every resource
      // Some calls to OData server won't return items with ID, or return items with an empty string as id
      // We add ID here to fit JSData requirement before JSData inject happens
      _.each(response.value as DataRecord[], (item: DataRecord, index: number) => {
        if (!_.has(item, definition.idAttribute) || item[definition.idAttribute] === '') {
          item[definition.idAttribute] = `${queryId}-${index}`;
        }
      });

      const addProperties = _.compose(addPropertyTotalCount, addPropertyRaw) as ObjectDecorator;

      if (options.cacheResponse) {
        options.afterInject = _.compose(
          options.afterInject || _.identity,
          (innerOptions: IODataOptions, injected: object) => addProperties(injected)
        ) as AfterInject;
      } else {
        // if odata response payload is not wrapped in value property,
        // move the contents under res.value
        response.value = _.has(res, 'value') ? response.value : response;
        addProperties(response.value as object);
      }

      return response.value;
    }, (res: { statusText: string }) =>
      // in case of error, it returns statusText for caller
      res.statusText);
  }

  update(definition: IODataDefinition, id: string | number, attrs: DataRecord, options: IODataOptions): Promise<unknown> {
    _.extendOwn(options, this.options);
    const data = odataCast.data({ data: attrs, definition, options });

    return odata.patch(
      odataUri.convert({ definition, options, id }),
      // NOTICE: modified to replace app state/config
      _.defaults({ data }, getCommonOptions(options))
    );
  }

  updateAll(definition: IODataDefinition, attrs: DataRecord, params: { reqs: IODataRequestParam[] }, options: IODataOptions): Promise<unknown> {
    _.extendOwn(options, this.options);

    // Must resolve the promise with the updated items
    return sendBatchRequest(definition, params.reqs, options);
  }

  destroy(definition: IODataDefinition, id: string | number, options: IODataOptions): Promise<unknown> {
    _.extendOwn(options, this.options);

    // Must return a promise
    return odata.delete(
      odataUri.convert({ definition, options, id }),
      getCommonOptions(options)
    ).then(_.property('value'));
  }

  destroyAll(definition: IODataDefinition, params: { reqs: IODataRequestParam[] }, options: IODataOptions): Promise<unknown> {
    _.extendOwn(options, this.options);

    return sendBatchRequest(definition, params.reqs, options);
  }
}
